import { catchQueryErrors } from '@/core/shared/helpers/ErrorHelper';
import { gql, gqlWithoutMiddleware } from '@/setup/api';
import { throttle, isEqual, cloneDeep, get } from 'lodash-es';
import moment from 'moment';
import { initialFilters } from './config';
import { loadLocale } from '@/core/shared/localization';
import { i18n } from '@/setup/i18n.js';
import router from '@/core/routes';
import Deferred from 'defer-promise';
import * as helpers from './plugins/timer/helpers';
import { tryRequest } from '@/core/shared/helpers/RetryHelper';
import scheduler from '@/launcher/services/scheduler';
import eventbus from '@/core/shared/services/EventBus';
import { createRevisionHelpers } from '@/launcher/helpers/version';
import { initEcho } from '@/setup/socket.js';
import * as Sentry from '@sentry/browser';

import {
  getGameQuery,
  getTutorialQuery,
  getStationQuery,
  getStationTagsQuery,
  getStationGamesQuery,
  getLocalesQuery,
  getCurrentTimeQuery,
  getBookingsQuery,
  getStationVideosQuery,
  getThemeParkGameQuery,
  // Mutations
  updateDevicesMutation,
  ratingMutation,
  storeGameSessionsMutation,
  createCoinOpBookingMutation,
} from './requests';
import { difficultyToInt, objListToArr, diffObjects } from '@/launcher/helpers';
import store from '../../core/store';
import { TIMER_EXPIRED_TIMEOUT } from './constants';
import Prefetcher from '../services/prefetch';
export * from './actions/CEFevents.js';
export * from './actions/update.js';
export * from './actions/updatedHooks.js';
export * from './actions/notify.js';
export * from './actions/timer.js';

export const getPusherConfiguration = async ({ commit, dispatch }) => {
  const { pusherConfiguration } = await gqlWithoutMiddleware.request(`
    query {
      pusherConfiguration {
        key
        host
        broadcaster
      }
    }
  `);
  commit('setPusherConfiguration', pusherConfiguration);
  return pusherConfiguration;
};

export const getGame = async ({ commit, dispatch }, id) => {
  commit('startLoading', 'getGame');
  try {
    const data = await getGameQuery(id);
    if (data) {
      dispatch('insertEntities', { name: 'Game', payload: data.game });
    }
    return data;
  } finally {
    commit('endLoading', 'getGame');
  }
};

export const getTutorial = async ({ commit, dispatch }, id) => {
  commit('startLoading', 'getTutorial');
  try {
    const data = await getTutorialQuery(id);
    if (data) {
      const tutorial = { ...data.tutorial, _tutorial: true };
      dispatch('insertEntities', { name: 'Game', payload: tutorial });
    }
    return data;
  } finally {
    commit('endLoading', 'getTutorial');
  }
};

export const getCurrentTime = async ({ commit }) => {
  const beforeQuery = performance.now();
  const data = await getCurrentTimeQuery();
  if (data) {
    const afterQuery = performance.now();
    const locationCurrentTime = moment(data.currentTime)
      .add(afterQuery - beforeQuery, 'milliseconds')
      .utc()
      .format();
    commit('setCurrentTime', locationCurrentTime);
    return locationCurrentTime;
  }
};

export const getStation = async ({ commit, getters, state, dispatch }) => {
  const { stationId } = getters;
  commit('startLoading', 'getStation');
  try {
    const data = await getStationQuery(stationId);
    if (data) {
      const { station } = data;
      dispatch('handleGetStationResponse', station);
    }
    return data;
  } finally {
    commit('endLoading', 'getStation');
  }
};

export const handleGetStationResponse = (
  { commit, getters, state, dispatch },
  station,
) => {
  // update local state
  dispatch('updateStationLocally', station);
};

// this is the same method as getStation except that this is used to batch the query along an initial mutation of the local state
export const getStationInitially = async ({
  commit,
  getters,
  state,
  dispatch,
  rootState,
}) => {
  const { currentStation, stationId } = getters;
  const { version, playing, playingType } = currentStation;
  commit('startLoading', 'getStation');
  try {
    const data = await dispatch('updateStation', {
      station: {
        id: stationId,
        version,
        playingId: playing && playing.id,
        playingType,
        safetyMode: false,
      },
      cancelKey: 'getStationInitially',
    });
    if (data) {
      const { station } = data;
      dispatch('handleGetStationResponse', station);
    }
    return data;
  } finally {
    commit('endLoading', 'getStation');
  }
};

export const diffStationAndRefresh = (
  { commit, getters, dispatch, state },
  station,
) => {
  const diff = diffObjects(getters.currentStation, station);
  if ('ageLimit' in diff) {
    dispatch('getStationTags');
  }
  if (diff.locale) {
    dispatch('changeLocale', station.locale);
  }
  if ('safetyMode' in diff && station.safetyMode === false) {
    dispatch('recoverFromSafetyMode');
  }
  dispatch('getStation');
};

let timeout;
export const getTodaysBookings = async ({
  commit,
  getters,
  dispatch,
  rootState,
}) => {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  const { currentStation } = getters;
  if (!currentStation) return;
  const data = await getBookingsQuery({
    locationId: currentStation.location.id,
    stationId: currentStation.id,
  });
  if (data) {
    const { bookingStationTimes } = data;
    commit('setBookingTimes', bookingStationTimes);
  }
  timeout = setTimeout(() => {
    dispatch('getTodaysBookings');
  }, 15000); // poll bookings every 15sec cause socket is unreliable
};

export const getStationVideos = async ({ commit, dispatch, getters }) => {
  const { stationId } = getters;
  commit('startLoading', 'stationVideos');
  try {
    const data = await getStationVideosQuery(stationId);
    if (data) {
      dispatch('insertEntities', {
        name: 'Video',
        payload: data.stationVideos.map((url, i) => ({ id: i, url })),
      });
      // commit('setStationVideos', data.stationVideos.map(({ id }) => id))
    }
    return data.stationVideos;
  } finally {
    commit('endLoading', 'stationVideos');
  }
};

export const prefetchGameImages = async ({
  commit,
  dispatch,
  getters,
  rootGetters,
}) => {
  // prefetch images
  const games = rootGetters['entities/Game/all']();
  games &&
    games.forEach(({ imageUrl }) => Prefetcher.fetch(imageUrl, { as: 'image' }));
};

export const getStationTags = async ({
  commit,
  dispatch,
  getters,
  rootGetters,
}) => {
  const { stationId } = getters;
  commit('startLoading', 'stationTags');
  try {
    const data = await getStationTagsQuery(stationId);
    if (data) {
      dispatch('insertEntities', {
        name: 'StationTag',
        payload: [...data.stationTags, ...data.stationLauncherTags],
      });
      commit('setStationTags', data.stationTags.map(({ id }) => id));
      commit(
        'setLauncherStationTags',
        data.stationLauncherTags.map(({ id }) => id),
      );
      dispatch('prefetchGameImages');
      return data;
    }
  } catch (e) {
    catchQueryErrors(e, () => {
      commit('setError', 'stationTags');
    });
  } finally {
    commit('endLoading', 'stationTags');
  }
};

export const getStationGames = async (
  { commit, dispatch, getters },
  filters = {},
) => {
  const { stationId } = getters;
  const {
    difficulty = [],
    tags = [],
    search = '',
    playerMode = { singleplayer: true, multiplayer: true },
  } = filters;
  const formattedDifficulties = objListToArr(difficulty, difficultyToInt);
  const data = await getStationGamesQuery(
    {
      stationId,
      difficulties: formattedDifficulties,
      tags,
      search,
      ...(playerMode.single ? { singleplayer: true } : {}),
      ...(playerMode.multi ? { multiplayer: true } : {}),
    },
    'filteredGames',
  );
  if (data) {
    dispatch('insertEntities', { name: 'Game', payload: data.stationGames });
    return data.stationGames;
  }
};

export const onTimeUp = async ({
  commit,
  getters,
  dispatch,
  rootState,
  state,
}) => {
  const { isGameRunning, currentStation } = getters;
  const { quitGameOnTimeUp } = currentStation;
  if (isGameRunning) {
    if (quitGameOnTimeUp) dispatch('startLauncher');
    else {
      dispatch('setDashboard', { visible: true, locked: true });
    }
  } else {
    dispatch('notifyTimesUp');
  }
  helpers.setTimeout(
    () => {
      dispatch('resetTimerExpiration');
    },
    { name: 'resetTimerExpiration', delay: TIMER_EXPIRED_TIMEOUT },
  );
};
export const onTimeStart = async ({
  commit,
  getters,
  state,
  dispatch,
  rootState,
}) => {
  helpers.clearTimeout('resetTimerExpiration');
  const { isGameRunning } = getters;
  dispatch('removeAllNotifications', { whitelist: ['timeadded'] });
  dispatch('onFiltersClear');
  const shouldRedirect =
    !isGameRunning ||
    (isGameRunning && rootState.route.meta.realName !== 'overlay');
  if (shouldRedirect) {
    if (!state.previousBooking) return; // don't redirect on first fetch
    dispatch('startLauncher');
  } else {
    dispatch('setDashboard', { locked: false });
  }
};
export const onTimeInfoAvailable = async ({
  commit,
  getters,
  state,
  dispatch,
  rootState,
}) => {
  const { isGameRunning, canStart } = getters;
  if (isGameRunning && !canStart) {
    dispatch('setDashboard', { visible: true, locked: true });
  }
};

export const flushFilteredGames = ({ commit, dispatch }) => {
  gql.cancelLast('filteredGames');
  commit('setFilteredGames', []);
};
export const flushSearchedGames = ({ commit, dispatch }) => {
  gql.cancelLast('filteredGames');
  commit('setSearchedGames', []);
};
export const clearSearch = ({ commit, dispatch }) => {
  dispatch('flushSearchedGames');
  dispatch('updateSearch', '');
};

export const startLoading = ({ commit }, key) => {
  commit('startLoading', key);
};
export const endLoading = ({ commit }, key) => {
  commit('endLoading', key);
};

export const getFilteredGames = async ({ state, commit, dispatch }) => {
  const { filters } = state;
  commit('startLoading', 'filteredGames');
  try {
    const data = await dispatch('getStationGames', filters);
    if (data) {
      // NOTE handle cancellation
      const stationGames = data;
      commit('setFilteredGames', stationGames.map(({ id }) => id));
    }
  } finally {
    commit('endLoading', 'filteredGames');
  }
};
export const getAvailablesLocales = async ({ state, commit, dispatch }) => {
  commit('startLoading', 'locales');
  const data = await getLocalesQuery();
  if (data) {
    // NOTE handle cancellation
    dispatch('insertEntities', { name: 'Locale', payload: data.locales });
    commit('endLoading', 'locales');
  }
};

export const onFiltersChange = async ({ commit, dispatch }, filters) => {
  commit('setFilters', filters);
  if (isEqual(filters, initialFilters)) {
    commit('disableFilters');
  } else {
    commit('enableFilters');
    return dispatch('getFilteredGames');
  }
};

export const onFiltersClear = ({ commit }) => {
  commit('disableFilters');
  commit('setFilters', cloneDeep(initialFilters));
  commit('endLoading', 'filteredGames');
};

export const getSearchedGames = throttle(async function getSearchedGames (
  { commit, dispatch },
  search,
) {
  commit('startLoading', 'filteredGames');
  const data = await dispatch('getStationGames', {
    search,
  });
  if (data) {
    const stationGames = data;
    commit('setSearchedGames', stationGames.map(({ id }) => id));
    commit('endLoading', 'filteredGames'); // NOTE handling cancellation
  }
},
1000);

export const onSearchChange = ({ state, commit, dispatch }) => {
  const { search = '' } = state;
  if (!search.length) {
    getSearchedGames.cancel();
    dispatch('flushSearchedGames');
    commit('disableSearch');
    commit('endLoading', 'filteredGames');
  } else {
    commit('enableSearch');
    dispatch('getSearchedGames', search);
  }
};

export const updateSearch = ({ commit, dispatch }, search) => {
  commit('setSearch', search);
  dispatch('onSearchChange');
};

export const updateFilters = ({ commit, dispatch }, search) => {
  commit('setSearch', search);
  dispatch('onSearchChange');
};

// i18n
export const changeLocale = async ({ commit, dispatch, getters }, locale) => {
  dispatch('startLoading', 'changeLocale');
  await Promise.all([
    dispatch('fetchTranslations', locale),
    dispatch('getStationTags'),
    dispatch('updateStationLocale', locale),
  ]);
  dispatch('endLoading', 'changeLocale');
};
export const updateStationLocale = async (
  { commit, dispatch, getters },
  locale,
) => {
  return dispatch('updateStationWithOptimistic', {
    payload: {
      locale,
    },
    loadingField: 'callingHelp',
  });
};
export const fetchTranslations = async (
  { commit, dispatch, getters },
  locale,
) => {
  dispatch('startLoading', 'fetchTranslations');
  commit('setLocale', locale);
  await loadLocale(i18n, locale, 'station');
  dispatch('endLoading', 'fetchTranslations');
};
export const initAppLocale = async ({ commit, dispatch, getters }) => {
  dispatch('startLoading', 'initAppLocale');
  const { currentStationLocale } = getters;
  await dispatch('fetchTranslations', currentStationLocale);
  dispatch('endLoading', 'initAppLocale');
};
export const getStationIdentity = async ({ commit, dispatch, getters }) => {
  const { token, version, id } = await dispatch('interop/getStationIdentity');
  commit('setToken', token);
  dispatch('updateStationLocally', { id, ...(version ? { version } : {}) }); // only save version when it is defined (aka only when fired from the launcher)
  if (version) {
    createRevisionHelpers(version);
    commit('setDesktopClientVersion', version);
  } else {
    createRevisionHelpers('latest'); // always default to latest version if no version is provided
    commit('setDesktopClientVersion', 'latest');
  }
  if (!router.currentRoute.params.stationId || router.currentRoute.params.stationId !== id) {
    router.push({ name: router.currentRoute.name, params: { stationId: id } });
  }
};
export const initApp = async ({
  commit,
  dispatch,
  getters,
  state,
  rootState,
}) => {
  const finishInitialization = async () => {
    commit('setInitializedFlag', true);
    try {
      await dispatch('interop/notifyAboutCurrentState');
    } finally {
      dispatch('endLoading', 'initApp');
    }
  };
  dispatch('startLoading', 'initApp');
  commit('resetError', 'initApp');
  initEcho({ state: rootState, commit, dispatch, getters });
  if (state.isAppInitialized) {
    eventbus.$emit('sentry:captureMessage', 'App already initialized');
  }
  // check if safety mode should still be on
  await dispatch('checkServiceHealth');
  if (!getters.isNetworkAvailable) {
    dispatch('activateSafetyMode');
    dispatch('restoreSafetyState');
    finishInitialization();
    return;
  }
  const waitForAppChanged = Deferred();
  // Here we wait for the first appChanged data as the only source of truth
  // otherwise it would launch game according to server state on init
  if (!state.appState) {
    const unsubscribe = store.subscribeAction(({ type, payload }) => {
      if (type === 'appChanged') {
        if (!payload.length) return;
        waitForAppChanged.resolve();
        unsubscribe();
      }
    });
  } else {
    waitForAppChanged.resolve();
  }
  await waitForAppChanged.promise;
  await tryRequest(async (retry, { remaining }) => {
    try {
      await Promise.all([
        dispatch('initTimer'),
        dispatch('getAvailablesLocales'),
        (async () => {
          await dispatch('getStationInitially');
          return Promise.all([
            dispatch('initAppLocale'),
            // dispatch('getTutorial', getters.tutorialId),
            dispatch('getTodaysBookings'),
            dispatch('getStationTags'),
          ]);
        })(),
      ]).catch(e => {
        return catchQueryErrors(e, () => {
          if (remaining) return retry();
          else throw e;
        });
      });
    } catch (e) {
      finishInitialization();
      commit('setError', 'initApp');
      eventbus.$emit('sentry:captureException', e);
      throw e;
    }
  });
  if (!getters.currentStation) {
    finishInitialization();
    commit('setError', 'initApp');
  }
  finishInitialization();
};

// Call help
export const callHelp = async ({ commit, dispatch, getters }) => {
  return dispatch('updateStationWithOptimistic', {
    payload: {
      calling: true,
    },
    loadingField: 'callingHelp',
  });
};
export const cancelHelp = async ({ commit, dispatch, getters }) => {
  return dispatch('updateStationWithOptimistic', {
    payload: {
      calling: false,
    },
    loadingField: 'callingHelp',
  });
};

// store previous route
export const storePreviousRoute = async (
  { commit, dispatch, getters },
  route,
) => {
  commit('storePreviousRoute', route);
};
// session
export const storeGameSessions = async (
  { commit, dispatch, getters },
  payload,
) => {
  try {
    const data = await storeGameSessionsMutation(payload);
    const sessionData = payload instanceof Array ? payload[1] : payload; // in case these are batched
    if (data) {
      commit('setPreviousSession', { ...sessionData, id: data.newSession.id });
      return data;
    } else {
      commit('setPreviousSession', null);
      Sentry.captureMessage('tracking request cancelled');
    }
  } catch (e) {
    catchQueryErrors(e, () => {
      commit('setPreviousSession', null);
      Sentry.captureMessage('tracking request error');
      Sentry.captureException(e);
    });
  }
};

export const updateStationPlayingWithOptimistic = async (
  { dispatch, getters },
  { playingId, playingType },
) => {
  const game = getters['entities/Game'](playingId);
  if (!game) {
    if (playingType === 'game') {
      if (playingId) dispatch('getGame', playingId);
    } else if (playingType === 'tutorial') {
      dispatch('getTutorial', playingId);
    }
  }
  return dispatch('updateStationWithOptimistic', {
    payload: { playing: { id: playingId }, playingType },
    loadingField: 'updateStation',
  });
};

export const confirmLaunchCancel = async (
  { dispatch, getters, state },
  { cds, launchMethod },
) => {
  const isGameStillLaunching = state.loadings.launchingGame;
  if (isGameStillLaunching) {
    if (cds || launchMethod === 'path') {
      router.push({
        name: 'games-confirm-cancel',
        query: { modalParentId: 'mainpanel' },
      });
      return;
    } else dispatch('interop/cancelLaunch');
  }
  router.goBack({ name: 'games-index' });
};
export const goBack = async ({ dispatch, getters, state }) => {
  router.goBack();
};
export const cancelLaunchAndRedirect = async ({ dispatch, getters, state }) => {
  dispatch('interop/cancelLaunch');
  router.push({ name: 'games-index' });
};

export const startGame = async (
  { dispatch, getters, state, rootGetters },
  { id, restartIfRunning },
) => {
  const startGameDelay = new Promise(resolve => {
    setTimeout(resolve, 2000);
  });
  dispatch('startLoading', 'launchingGame');
  dispatch('onFiltersClear');
  dispatch('removeAllNotifications');
  dispatch('addNotification', {
    delay: null,
    props: {
      message: i18n.t('Launching game'),
      duration: 3000,
      type: 'loading',
      soundEnable: false,
    },
  });
  let game = rootGetters['entities/Game'](id);
  if (!game) {
    const data = await dispatch('getGame', id);
    game = data.game;
  }
  try {
    // we call this every time cause background service prevents infinite loop
    await startGameDelay;
    const resp = dispatch('interop/startGame', {
      id,
      type: 'game',
      restartIfRunning,
    });
    if (resp) {
      const { result } = resp;
      dispatch('removeAllNotifications');
      if (result === 'Failed' || result === 'TimedOut') {
        dispatch('addNotification', {
          delay: null,
          props: {
            duration: 7000,
            message: i18n.t(
              '{game} failed to launch. Try again or contact the operator',
              { game: game.title },
            ),
            type: 'error',
          },
        });
      }
      // dispatch('endLoading', 'launchingGame')
    }
  } catch (e) {
    // dispatch('endLoading', 'launchingGame')
  }
};
export const startGameWithRestart = (
  { dispatch, getters, state, rootGetters },
  id,
) => {
  dispatch('startGame', { id, restartIfRunning: true });
};
export const startTutorial = async ({ dispatch, getters, state }) => {
  dispatch('addNotification', {
    props: {
      message: i18n.t('Launching Tutorial'),
      type: 'loading',
      soundEnable: false,
    },
  });
  // we call this every time cause background service prevents infinite loop
  dispatch('interop/startGame', { id: getters.tutorialId, type: 'tutorial' });
};
export const startLauncher = ({ dispatch, getters, state }, opts) => {
  // we call this every time cause background service prevents infinite loop
  const launcherId = getters.currentStation.experience.launcher.id;
  dispatch('setDashboard', { locked: false });
  dispatch('interop/startGame', { id: launcherId, type: 'launcher', ...opts });
};
export const startLauncherWithRestart = ({ dispatch, getters, state }) => {
  dispatch('startLauncher', { restartIfRunning: true });
};

export const updateVolumeWithOptimistic = async (
  { dispatch, getters },
  volume,
) => {
  return dispatch('updateStationWithOptimistic', {
    payload: { volume },
    loadingField: 'updateStationVolume',
  });
};
export const setAudio = async ({ dispatch, getters }, volume) => {
  dispatch('updateVolumeWithOptimistic', volume);
  dispatch('interop/setAudio', { volume });
};

export const updateDevices = async ({ dispatch, getters }, devices) => {
  const { stationId } = getters;
  const data = await updateDevicesMutation(stationId, devices);
  return data;
};

export const registerPanel = async ({ commit, getters, state }, panel) => {
  const isSame = isEqual(panel, state.panels.find(({ id }) => id === panel.id));
  if (!isSame) commit('registerPanel', panel);
};
export const unregisterPanel = async ({ commit, getters }, id) => {
  commit('unregisterPanel', id);
};

export const hideDashboard = async ({ dispatch, commit }) => {
  dispatch('setDashboard', { visible: false });
};
export const setDashboard = async (
  { dispatch, commit },
  { visible, locked },
) => {
  if (visible !== undefined) commit('setSessionOverlay', visible);
  dispatch('interop/setDashboard', { visible, locked });
};
export const donePlaying = async ({ commit, state, dispatch, getters }) => {
  const { runningType, runningId, state: appStateState } = state.appState;
  const { currentStation, safetyMode } = getters;
  const shouldSkipRating =
    !runningId ||
    runningType === 'tutorial' ||
    runningType === 'unknown_game' ||
    appStateState === 'launching' ||
    !get(currentStation, 'experience.gameReviewsEnabled') ||
    safetyMode;
  if (!shouldSkipRating) {
    if (!getters.canStart) dispatch('redirectToLauncherWithNotification');
    commit('setReviewingGameId', runningId);
    router.push({ name: 'review' });
  } else {
    dispatch('startLauncher');
  }
};
export const rateGame = async (
  { commit, dispatch, getters, state },
  rating,
) => {
  const { canStart } = getters;
  const { stationId, currentBooking, currentCustomer } = getters;
  if (rating !== undefined && rating !== null && state.reviewingGameId) {
    ratingMutation({
      game: { id: state.reviewingGameId },
      booking: { id: currentBooking && currentBooking.id },
      station: { id: stationId },
      customer: { id: currentCustomer && currentCustomer.id },
      value: rating,
    });
  }
  commit('setReviewingGameId', null);
  if (!canStart) {
    router.push({ name: 'thanks' });
  } else {
    const action = () => {
      dispatch('startLauncher');
    };
    if (rating !== undefined) {
      setTimeout(action, 2000);
    } else {
      action();
    }
  }
};
export const resetTimerExpiration = ({ commit, dispatch }) => {
  commit('registerPreviousBooking', null);
  dispatch('removeAllNotifications');
};

export const fetchRestoredData = ({ commit, dispatch, getters }) => {
  if (!scheduler.isInitialized) {
    dispatch('initTimer');
  } else {
    dispatch('getCurrentTime');
  }
  dispatch('getAvailablesLocales');
  dispatch('initAppLocale');
  dispatch('getStationTags');
  dispatch('getTodaysBookings');
};

export const restoreSafetyState = async ({ rootState, commit }) => {
  if (!rootState.storedLauncherState) {
    commit('setError', 'safetyModeWithoutPersistedState');
    return;
  }
  store.replaceState({
    ...rootState,
    launcher: rootState.storedLauncherState,
  });
};
export const activateSafetyMode = async ({
  commit,
  dispatch,
  getters,
  rootState,
}) => {
  gql.cancelLast('getStation');
  dispatch('updateStationLocally', {
    safetyMode: true,
  });
};
export const onSafetyModeOn = async ({
  commit,
  dispatch,
  getters,
  rootState,
}) => {
  dispatch('notifySafetyModeOn');
  if (rootState.route.meta.realName === 'games-search') {
    router.push({ name: 'games-index' });
  }
  dispatch('onFiltersClear');
  dispatch('updateStationLocally', {
    calling: false,
    idleMode: null,
  });
  commit('setPreviousSession', null); // clear session on safety on
};
export const onSafetyModeOff = async ({
  commit,
  dispatch,
  getters,
  rootState,
}) => {
  dispatch('notifySafetyModeOff');
};
export const recoverFromSafetyMode = async ({ commit, dispatch, getters }) => {
  commit('setRecoveringFlag', true);
  try {
    const { currentStation, stationId } = getters;
    const { version, playing, playingType, volume } = currentStation;
    await dispatch('updateStation', {
      station: {
        id: stationId,
        version,
        playingId: playing && playing.id,
        playingType,
        volume,
        safetyMode: false,
        calling: false,
      },
      loadingField: 'recoverFromSafetyMode',
      cancelKey: 'recoverFromSafetyMode',
      withoutMiddleware: true,
    });
  } finally {
    commit('setRecoveringFlag', false);
  }
  dispatch('fetchRestoredData');
};
export const getThemeParkGameId = async ({ commit, dispatch, getters }) => {
  const { stationId } = getters;
  commit('startLoading', 'themeParkGameId');
  try {
    const data = await getThemeParkGameQuery(stationId);
    if (data) {
      const {
        stationThemeParkGame: { id },
      } = data;
      commit('setThemeParkGameId', id);
      return id;
    }
  } finally {
    commit('endLoading', 'themeParkGameId');
  }
};
export const createCoinOpBooking = async ({ commit, dispatch, getters }) => {
  const { stationId } = getters;
  await createCoinOpBookingMutation(stationId);
};
